import * as React from "react";
import axios from "axios";
import { config } from "../../../config/config.jsx";
import CryptoJS from "crypto-js";
import { format } from "date-fns"; // Add this import for date formatting
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/solid";
import { Empty, Modal } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Import slip images
import CoatofArm from "../../assets/images/coat-of-arm.png";
import BVNlogo from "../../assets/images/BVN-logo.png";
import AvatarFallback from "../../assets/images/BVN-logo.png";
import AdvancedAvatar from "../../assets/images/1.png";
import Fingerprint from "../../assets/images/finger-print.png";

// Add your secret key for decryption
const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

export default function VerificationsHistoryTable() {

  // Get encrypted user data from localStorage
  const encryptedUser = localStorage.getItem("user");
  const user = decryptData(encryptedUser);
  const userId = user?._id || user?.id;

  // Set the API link using the userId
  const apiLink = `${config.apiBaseUrl}${config.endpoints.DataHistory}${userId}`;

  const [loading, setLoading] = React.useState(false);
  const [apiData, setApiData] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [sortConfig, setSortConfig] = React.useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [pageSize, setPageSize] = React.useState(10);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [downloadingId, setDownloadingId] = React.useState(null);

  const fetchVerificationHistory = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await axios.get(apiLink, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const details = response.data?.findData || [];
      setApiData(details || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions based on search term and only show credit transactions
  // Modify the filteredTransactions to only show BVN-Slip data
  const filteredTransactions = apiData.filter((transaction) => {
    // First filter for BVN-Slip only
    if (transaction.dataFor !== "BVN-Slip") return false;

    const searchStr = searchTerm.toLowerCase();
    const transactionDate = new Date(transaction.createdAt);

    // Date filter
    const passesDateFilter =
      (!startDate || transactionDate >= startDate) &&
      (!endDate || transactionDate <= endDate);

    // Text search filter for BVN specific fields
    const passesSearchFilter =
      transaction.slipLayout?.toLowerCase().includes(searchStr) ||
      transaction.data?.verification?.reference
        ?.toLowerCase()
        .includes(searchStr) ||
      transaction.data?.verification?.status
        ?.toLowerCase()
        .includes(searchStr) ||
      transaction.data?.data?.firstName?.toLowerCase().includes(searchStr) ||
      transaction.data?.data?.lastName?.toLowerCase().includes(searchStr) ||
      transaction.data?.detail?.toLowerCase().includes(searchStr);

    return passesDateFilter && passesSearchFilter;
  });

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      if (sortConfig.key === "createdAt") {
        const dateA = new Date(a[sortConfig.key]);
        const dateB = new Date(b[sortConfig.key]);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Create a reusable header component
  const TableHeader = ({ label, sortKey, className }) => {
    const isSorted = sortConfig.key === sortKey;
    const icon = isSorted ? (
      sortConfig.direction === "asc" ? (
        <ArrowUpIcon className="w-4 h-4 text-blue-600" />
      ) : (
        <ArrowDownIcon className="w-4 h-4 text-blue-600" />
      )
    ) : (
      <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
    );

    return (
      <th
        scope="col"
        className={`${className} cursor-pointer hover:bg-gray-50`}
        onClick={() => sortKey && sortData(sortKey)}
      >
        <div className="flex items-center justify-between">
          <span>{label}</span>
          <span className="ml-2">{icon}</span>
        </div>
      </th>
    );
  };

  const showModal = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalVisible(true);
  };

  // Build Basic BVN slip HTML for off-screen rendering
  const buildBasicSlipHTML = (userData) => {
    const avatarSrc = userData.base64Image
      ? `data:image/jpeg;base64,${userData.base64Image}`
      : AvatarFallback;

    return `
      <div style="background:white;padding:32px;width:850px;font-family:Arial,sans-serif;">
        <div style="display:flex;justify-content:center;align-items:center;gap:20px;">
          <img src="${CoatofArm}" alt="Coat of Arms" style="width:92px;height:60px;" crossorigin="anonymous" />
          <p style="font-size:18px;color:#4B5563;font-weight:bold;">Federal Republic of Nigeria</p>
          <img src="${BVNlogo}" alt="BVN logo" style="width:80px;height:48px;" crossorigin="anonymous" />
        </div>
        <h2 style="font-size:18px;color:#4B5563;font-weight:bold;text-align:center;margin:8px 0;">Verified BVN Details</h2>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;">
            <div style="display:flex;gap:24px;margin-top:12px;">
              <div>
                ${[
                  ["First Name", userData.firstName],
                  ["Middle Name", userData.middleName],
                  ["Last Name", userData.lastName],
                  ["Date of Birth", userData.dateOfBirth],
                  ["Gender", userData.gender],
                ].map(([label, val]) => `
                  <div style="display:flex;margin-bottom:8px;">
                    <span style="color:#374151;font-weight:600;width:80px;font-size:11px;">${label}:</span>
                    <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                  </div>
                `).join("")}
                <div style="margin-top:20px;display:flex;gap:24px;">
                  <div>
                    ${[
                      ["Marital Status", userData.maritalStatus],
                      ["Phone Number", userData.phoneNumber1],
                      ["Enrollment Institution", userData.enrollmentBank],
                      ["Origin State", userData.stateOfOrigin],
                      ["Residence State", userData.stateOfResidence],
                      ["Residential Address", userData.residentialAddress],
                    ].map(([label, val]) => `
                      <div style="display:flex;margin-bottom:8px;">
                        <span style="color:#374151;font-weight:600;width:80px;font-size:11px;">${label}:</span>
                        <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                      </div>
                    `).join("")}
                  </div>
                  <div>
                    ${[
                      ["NIN", userData.nin],
                      ["Enrollment Branch", userData.enrollmentBranch],
                      ["Origin LGA", userData.lgaOfOrigin],
                      ["Residence LGA", userData.lgaOfResidence],
                    ].map(([label, val]) => `
                      <div style="display:flex;margin-bottom:8px;">
                        <span style="color:#374151;font-weight:600;width:80px;font-size:11px;">${label}:</span>
                        <span style="color:#4B5563;font-size:11px;">${val || "-"}</span>
                      </div>
                    `).join("")}
                  </div>
                </div>
              </div>
              <div style="display:flex;flex-direction:column;align-items:center;width:145px;">
                <img src="${avatarSrc}" alt="Profile" style="width:80px;height:100px;object-fit:cover;" crossorigin="anonymous" />
                <p style="text-align:center;font-size:10px;margin-top:8px;color:#374151;font-weight:600;">BVN</p>
                <p style="text-align:center;font-size:16px;margin-top:4px;color:#374151;font-weight:600;">${userData.bvn || "-"}</p>
              </div>
            </div>
          </div>
          <div style="flex:1;padding-left:16px;">
            <h4 style="color:#16A34A;font-size:18px;text-align:center;">Verified</h4>
            <p style="font-size:10px;color:#4B5563;text-align:center;">Please do note that:</p>
            <ol style="font-weight:600;font-size:10px;color:#4B5563;margin-top:8px;padding-left:16px;">
              <li style="margin-bottom:8px;">The information on this slip remains valid until altered/modified where necessary by an authorized body</li>
              <li style="margin-bottom:8px;">Any person/authority using the information should verify it at anyverify.com.ng or any other channel approved by the federal government of Nigeria.</li>
              <li style="margin-bottom:8px;">The information shown on this slip is valid for the lifetime of the holder and <span style="color:#EF4444;font-weight:bold;">DOES NOT EXPIRE</span>.</li>
              <li style="margin-bottom:8px;">AnyVerify should not be blamed for any unauthorized alteration/copy/erasure etc. done on the slip</li>
            </ol>
          </div>
        </div>
      </div>
    `;
  };

  // Build Advanced BVN slip HTML for off-screen rendering
  const buildAdvancedSlipHTML = (userData) => {
    const avatarSrc = userData.base64Image
      ? `data:image/jpeg;base64,${userData.base64Image}`
      : AdvancedAvatar;

    const issuedDate = userData.verification_details?.verified_at
      ? format(new Date(userData.verification_details.verified_at), "dd/MM/yyyy")
      : "-";

    return `
      <div style="background:white;padding:28px 48px;width:672px;font-family:Arial,sans-serif;border:1px solid #e5e7eb;border-radius:8px;position:relative;">
        <div style="margin-bottom:16px;">
          <img src="${BVNlogo}" alt="BVN Logo" style="width:280px;height:160px;" crossorigin="anonymous" />
        </div>
        <div style="display:flex;align-items:flex-start;margin-bottom:16px;">
          <img src="${avatarSrc}" alt="User Avatar" style="width:160px;height:auto;margin-right:16px;" crossorigin="anonymous" />
          <div>
            <p style="color:#9CA3AF;font-size:17px;font-weight:600;">SURNAME</p>
            <p style="color:#111827;font-size:18px;font-weight:600;">${userData.lastName || "-"}</p>
            <p style="color:#9CA3AF;font-size:17px;font-weight:600;margin-top:12px;">FIRST NAME/ OTHER NAME</p>
            <p style="color:#111827;font-size:18px;font-weight:600;">${userData.firstName || "-"} ${userData.middleName || ""}</p>
            <div style="display:flex;gap:40px;margin-top:12px;">
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">DATE OF BIRTH</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${userData.dateOfBirth || "-"}</p>
              </div>
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">GENDER</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${userData.gender || "-"}</p>
              </div>
              <div>
                <p style="color:#9CA3AF;font-size:16px;font-weight:600;">ISSUED DATE</p>
                <p style="color:#111827;font-size:18px;font-weight:600;">${issuedDate}</p>
              </div>
            </div>
          </div>
        </div>
        <div style="text-align:center;margin-top:16px;">
          <p style="color:#9CA3AF;font-size:18px;font-weight:600;">Bank Verification Number (BVN)</p>
          <p style="color:#111827;font-size:30px;font-weight:600;">${userData.bvn || "-"}</p>
        </div>
        <div style="position:absolute;top:48px;right:0;display:flex;align-items:center;gap:24px;">
          <div>
            <img src="${Fingerprint}" alt="Fingerprint" style="width:168px;height:260px;margin-right:16px;" crossorigin="anonymous" />
          </div>
        </div>
      </div>
    `;
  };

  // Download slip as PDF directly using an isolated iframe to avoid oklch CSS errors
  const handleDownloadSlip = async (transaction) => {
    const transactionId = transaction._id;
    setDownloadingId(transactionId);

    let iframe = null;

    try {
      const slipType = transaction.slipLayout;
      const userData = transaction.data?.data;

      if (!userData) {
        return;
      }

      // Build the slip HTML based on type
      const slipHTML = slipType === "Basic"
        ? buildBasicSlipHTML(userData)
        : buildAdvancedSlipHTML(userData);

      // Create an isolated iframe to avoid page CSS (oklch) leaking in
      iframe = document.createElement("iframe");
      iframe.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:1800px;height:1200px;border:none;visibility:hidden;";
      document.body.appendChild(iframe);

      // Set iframe internal width via attribute so content lays out at full width
      iframe.setAttribute("width", "1800");
      iframe.setAttribute("height", "1200");

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              html, body { background: white; font-family: Arial, sans-serif; width: 100%; }
            </style>
          </head>
          <body>${slipHTML}</body>
        </html>
      `);
      iframeDoc.close();

      // Wait for images inside the iframe to load
      const images = iframeDoc.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve();
              else {
                img.onload = resolve;
                img.onerror = resolve;
              }
            })
        )
      );

      // Small delay to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Capture with html2canvas inside the iframe
      const targetEl = iframeDoc.body.firstElementChild;
      // Get actual rendered size of the element
      const elWidth = targetEl.scrollWidth;
      const elHeight = targetEl.scrollHeight;

      const canvas = await html2canvas(targetEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: elWidth,
        height: elHeight,
        windowWidth: elWidth + 100,
        windowHeight: elHeight + 100,
      });

      // Generate PDF — size PDF to match the actual content proportions
      const imgData = canvas.toDataURL("image/png");
      const canvasW = canvas.width;
      const canvasH = canvas.height;

      // Convert px to mm at 96 DPI (scale=2 means canvas is 2x, so real px = canvas/2)
      const pxToMm = 25.4 / 96;
      const pdfWidth = (canvasW / 2) * pxToMm + 10; // +10mm margin
      const pdfHeight = (canvasH / 2) * pxToMm + 10;

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "mm",
        format: [pdfWidth, pdfHeight],
      });

      pdf.addImage(imgData, "PNG", 5, 5, pdfWidth - 10, pdfHeight - 10);

      // Download with a descriptive filename
      const bvn = userData.bvn || "unknown";
      const firstName = userData.firstName || "";
      const lastName = userData.lastName || "";
      const fileName = `BVN_Slip_${firstName}_${lastName}_${bvn}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      
    } finally {
      // Clean up iframe
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      setDownloadingId(null);
    }
  };

  React.useEffect(() => {
    fetchVerificationHistory();
  }, [userId]);

  const sortedTransactions = getSortedData(filteredTransactions);

  // Pagination logic
  const paginatedData = sortedTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(sortedTransactions.length / pageSize);

  return (
    <div className="p-4 w-full">
      <h2 className="text-[clamp(1.2rem,2vw,2rem)] font-bold mb-4">
        BVN Verification History
      </h2>



      

      {/* Loading Spinner */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="text-gray-500 text-sm">
              Loading BVN verification history...
            </p>
          </div>
        </div>
      )}

      {!loading && sortedTransactions.length > 0 ? (
        <div className="relative overflow-hidden rounded-lg border border-gray-200 shadow">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <table className="w-full table-auto divide-y divide-gray-200 transition-all duration-300 ease-in-out min-w-[800px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[60px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    S/N
                  </th>
                  <th className="w-[120px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    NIN
                  </th>
                  <th className="w-[100px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    STATUS
                  </th>
                  <th className="w-[100px] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    Download slip
                  </th>
                  <TableHeader
                    label="Date"
                    sortKey="createdAt"
                    className="w-[clamp(80px,15vw,112px)] px-2 py-2 text-left text-[clamp(0.65rem,1vw,0.75rem)] font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                  />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((transaction, index) => (
                  <tr
                    key={transaction._id || index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="w-[60px] px-2 py-2 whitespace-nowrap text-[clamp(0.8rem,1vw,0.75rem)] text-gray-900">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="w-[120px] py-2 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm font-bold tracking-widest capitalize text-gray-800">
                        {transaction.data?.data?.bvn ? String(transaction.data.data.bvn) : "N/A"}
                      </span>
                    </td>
                    <td className="w-[100px] py-2 whitespace-nowrap">
                      <span className={`mt-1 text-sm font-medium capitalize px-2 py-0.5 rounded-full inline-block ${['success', 'successful'].includes(String(transaction.data?.verification?.status || "").toLowerCase()) ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {transaction.data?.verification?.status || "N/A"}
                      </span>
                    </td>
                    <td className="w-[100px] px-2 py-2 whitespace-nowrap">
                      {transaction.dataFor !== "IPE-Slip" ? (
                        <button
                          onClick={() => handleDownloadSlip(transaction)}
                          disabled={downloadingId === transaction._id}
                          className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors disabled:opacity-50"
                          title="Download BVN Slip"
                        >
                          {downloadingId === transaction._id ? "Downloading..." : "[download]"}
                        </button>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="w-[clamp(80px,15vw,112px)] px-2 py-2 whitespace-nowrap text-[clamp(0.8rem,1vw,0.75rem)] text-gray-900">
                      {format(
                        new Date(transaction.createdAt),
                        "dd/MM/yyyy"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : !loading ? (
        <div className="flex flex-col items-center justify-center p-8">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{ height: 60 }}
            description={
              <div className="text-center">
                <p className="text-gray-500 text-lg mb-2">
                  No Transaction Records Found
                </p>
                <p className="text-gray-400 text-sm">
                  Your transaction history will appear here
                </p>
              </div>
            }
          />
        </div>
      ) : null}

      {/* Pagination Controls - only show when not loading and has data */}
      {!loading && sortedTransactions.length > 0 && (
        <div className="mt-4 flex flex-col lg:flex-row gap-4 items-center justify-between">
          {/* Rows per page and showing entries */}
          <div className="flex flex-col sm:flex-row items-center gap-2 w-full lg:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 whitespace-nowrap">
                Rows per page:
              </span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded-md px-2 py-1 text-sm transition-colors duration-200 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="text-sm text-gray-700 text-center sm:text-left">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, sortedTransactions.length)} of{" "}
              {sortedTransactions.length} entries
            </span>
          </div>

          {/* Pagination buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md transition-all duration-200 ease-in-out transform hover:scale-105 min-w-[80px] ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              Previous
            </button>

            <div className="flex flex-wrap items-center gap-1 max-w-[calc(100vw-2rem)] overflow-x-auto">
              {totalPages <= 7 ? (
                // Show all pages if total pages are 7 or less
                [...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded-md transition-all duration-200 ease-in-out transform hover:scale-105 ${
                      currentPage === i + 1
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))
              ) : (
                // Show truncated pagination for more than 7 pages
                <>
                  {[...Array(Math.min(3, totalPages))].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded-md transition-all duration-200 ease-in-out transform hover:scale-105 ${
                        currentPage === i + 1
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  {currentPage > 4 && <span className="px-2">...</span>}
                  {currentPage > 3 && currentPage < totalPages - 2 && (
                    <button className="px-3 py-1 rounded-md bg-blue-500 text-white">
                      {currentPage}
                    </button>
                  )}
                  {currentPage < totalPages - 3 && (
                    <span className="px-2">...</span>
                  )}
                  {[...Array(Math.min(2, totalPages))].map((_, i) => (
                    <button
                      key={totalPages - 1 + i}
                      onClick={() => setCurrentPage(totalPages - 1 + i)}
                      className={`px-3 py-1 rounded-md transition-all duration-200 ease-in-out transform hover:scale-105 ${
                        currentPage === totalPages - 1 + i
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      {totalPages - 1 + i}
                    </button>
                  ))}
                </>
              )}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md transition-all duration-200 ease-in-out transform hover:scale-105 min-w-[80px] ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      <Modal
        title="Verification Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <div className="space-y-4">
            {selectedTransaction.dataFor === "IPE-Slip" ? (
              // IPE-Slip specific details
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {format(
                      new Date(selectedTransaction.createdAt),
                      "dd/MM/yyyy"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <span className="mt-1 text-sm font-medium capitalize px-2 py-0.5 rounded-full inline-block bg-green-100 text-green-800">
                    {selectedTransaction.data?.transactionStatus || "N/A"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Name</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.reply?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Date of Birth
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.reply?.dob || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">New NIN</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.newNIN || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    New Tracking ID
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.newTracking_id || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Old Tracking ID
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.old_tracking_id || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Verification Status
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.verificationStatus || "N/A"}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500">Message</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.message || "N/A"}
                  </p>
                </div>
              </div>
            ) : (
              // Existing modal content for other verification types
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {format(
                      new Date(selectedTransaction.createdAt),
                      "dd/MM/yyyy"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Data For</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.dataFor} -{" "}
                    {selectedTransaction.slipLayout}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Verification Type
                  </p>
                  <p className="mt-1 uppercase text-sm text-gray-900">
                    {selectedTransaction.verifyWith}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <span className="mt-1 text-sm font-medium capitalize px-2 py-0.5 rounded-full inline-block bg-blue-100 text-blue-800">
                    {selectedTransaction.data.verification.status || "N/A"}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Reference</p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.verification?.reference || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Response Details
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    <span className="mt-2">
                      {selectedTransaction.dataFor !== "IPE-Slip" ? (
                        <button
                          onClick={() => handleDownloadSlip(selectedTransaction)}
                          disabled={downloadingId === selectedTransaction._id}
                          className="text-green-600 border p-1 rounded hover:text-green-800 transition-colors disabled:opacity-50"
                        >
                          {downloadingId === selectedTransaction._id ? "Downloading..." : "Download Slip"}
                        </button>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </span>
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-gray-500">
                    Response Message
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTransaction.data?.message || "N/A"}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
